import FocusedTask from '@atlassian/focused-task';
import { breakpointValues, useResponsiveBreakpoint } from '@atlassian/mpac-primitives';
import { isCompatibleMarketplaceProduct } from '@atlassian/mpac-services-client';
import { SiteSelector, useGetCmtAvailability } from '@atlassian/mpac-site-selection';
import { MarketplaceAppPaymentModel } from '@atlassian/mpac-types/graphql-gateway-types';
import { ErrorBoundary } from '@atlassian/mpac-ui';
import { useState } from 'react';
import { IntlProvider } from 'react-intl-next';
import { TEST_IDS } from '../constants';
import { FocusedTaskErrorBoundaryFallback } from '../FocusedTaskErrorBoundary';
import { resolveInstallationCTALabel } from '../utils';
import { ReviewAndInstall } from './ReviewAndInstall';
import { useTwoClickAppInstall } from './useTwoClickAppInstall';
import { jsx as _jsx } from "react/jsx-runtime";
// eslint-disable-next-line max-lines-per-function
export const TwoClickAppInstallFocusedTask = props => {
  var _hostStatusResponse$h;
  const {
    isFocusedTaskOpen,
    handleFocusedTaskClose,
    links,
    appHeaderDetails,
    appDetails,
    compatibleProducts,
    mpacBaseUrl,
    stargateBasePath,
    installAppHrefResolver,
    requestAppHrefResolver,
    onSiteSelectorFooterInstallCTAClick
  } = props;
  const {
    value: breakpoint
  } = useResponsiveBreakpoint();
  const isMobile = breakpoint < breakpointValues.sm;
  const {
    hostStatusResponse,
    installedAppDetailsResponse: {
      installedAppDetailsResult
    },
    selectedSiteAndProduct
  } = useTwoClickAppInstall({
    appKey: appDetails.appKey
  });
  const {
    hostCmtEnabled,
    billingSystem
  } = ((_hostStatusResponse$h = hostStatusResponse.hostStatusResult) === null || _hostStatusResponse$h === void 0 ? void 0 : _hostStatusResponse$h.marketplaceStore.hostStatus) || {};
  const {
    cmtAvailabilityResult
  } = useGetCmtAvailability(appDetails.appKey, billingSystem, hostCmtEnabled, selectedSiteAndProduct);
  const [currentStep, setCurrentStep] = useState(0);
  const handleOnClose = () => {
    setCurrentStep(0);
    handleFocusedTaskClose();
  };
  const handleCancelButtonClick = () => {
    if (currentStep <= 0) {
      handleFocusedTaskClose();
    } else {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleReviewButtonClick = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const onSiteSelection = siteProductSelected => {
    console.log(siteProductSelected);
  };
  const resolveInstallationCTA = () => {
    var _installedAppDetailsR;
    return resolveInstallationCTALabel({
      isFreeApp: appDetails.paymentModel === MarketplaceAppPaymentModel.Free,
      isCloudMigrationTrialEligible: !!(cmtAvailabilityResult !== null && cmtAvailabilityResult !== void 0 && cmtAvailabilityResult.marketplaceStore.cmtAvailability.allowed),
      isEligibleForFreeOnSandbox: !!(installedAppDetailsResult !== null && installedAppDetailsResult !== void 0 && (_installedAppDetailsR = installedAppDetailsResult.marketplaceStore.installedAppDetails) !== null && _installedAppDetailsR !== void 0 && _installedAppDetailsR.paidLicenseActiveOnParent)
    });
  };
  const steps = [{
    id: 'select-a-site-step',
    label: 'Select a site',
    type: isMobile ? 'information' : 'progress',
    content: /*#__PURE__*/_jsx(SiteSelector, {
      appDetails: appDetails,
      appCompatibilities: compatibleProducts.filter(product => isCompatibleMarketplaceProduct(product)),
      mpacBaseUrl: mpacBaseUrl,
      stargateBasePath: stargateBasePath,
      links: links,
      onClose: handleCancelButtonClick,
      installAppHrefResolver: installAppHrefResolver,
      requestAppHrefResolver: requestAppHrefResolver,
      hidePrivacyContainerAndSiteFooter: true,
      onChange: onSiteSelection,
      onSiteSelectorFooterInstallCTAClick: onSiteSelectorFooterInstallCTAClick
    }),
    backButtonText: 'Cancel',
    backButtonOnClick: handleCancelButtonClick,
    nextButtonText: 'Review',
    nextButtonOnClick: handleReviewButtonClick,
    contentSize: 'large'
  }, {
    id: 'review-and-install-step',
    label: 'Review and install',
    type: isMobile ? 'information' : 'progress',
    content: /*#__PURE__*/_jsx(ReviewAndInstall, {
      appHeaderDetails: appHeaderDetails,
      links: links,
      appDetails: appDetails,
      hostStatusResponse: hostStatusResponse,
      installedAppDetailsResult: installedAppDetailsResult,
      cmtAvailabilityResult: cmtAvailabilityResult
    }),
    backButtonText: 'Back',
    backButtonOnClick: handleCancelButtonClick,
    nextButtonText: resolveInstallationCTA(),
    nextButtonOnClick: handleReviewButtonClick,
    contentSize: 'large'
  }];
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "TwoClickAppInstallMpac",
    fallback: /*#__PURE__*/_jsx(FocusedTaskErrorBoundaryFallback, {
      isOpen: isFocusedTaskOpen,
      onClose: handleOnClose
    }),
    children: /*#__PURE__*/_jsx(IntlProvider, {
      locale: "en",
      children: /*#__PURE__*/_jsx(FocusedTask, {
        testId: TEST_IDS.TWO_CLICK_APP_INSTALL_MPAC_FOCUSED_TASK,
        currentStep: currentStep,
        isOpen: isFocusedTaskOpen,
        steps: steps,
        onClose: handleOnClose
      })
    })
  });
};